import { Machine as GeneratedMachine } from '../../gql/generated';
import {
  SidePanel,
  SidePanelTab,
  SidePanelTabContent,
  SidePanelTabList,
  SidePanelTabs,
} from '../SidePanel';
import { MachineDetailsTab } from './MachineDetailsTab';
import { MachineEventLogTab } from './MachineEventLogTab';

const machineTabOrder = ['event-log', 'details'];

// Use the generated Machine type from GraphQL
type Machine = GeneratedMachine;

interface MachineSidePanelProps {
  /** Whether the panel is open */
  open: boolean;
  /** Callback when the open state changes */
  onOpenChange: (open: boolean) => void;
  /** The machine to display details for */
  machine: Machine | null;
}

/**
 * MachineSidePanel - Side panel for viewing machine details
 *
 * Displays machine information in a slide-out panel with two tabs:
 * - Details: Basic machine information, customer, plant, and technical info
 * - Pulse alerts: History of alarms and events for this machine
 *
 * @example
 * ```tsx
 * const [selectedMachine, setSelectedMachine] = useState<Machine | null>(null);
 *
 * <MachineSidePanel
 *   open={!!selectedMachine}
 *   onOpenChange={(open) => !open && setSelectedMachine(null)}
 *   machine={selectedMachine}
 * />
 * ```
 */
export function MachineSidePanel({
  open,
  onOpenChange,
  machine,
}: MachineSidePanelProps) {
  if (!machine) return null;

  // Build subtitle from the customer record's name and the plant
  const subtitleParts: string[] = [];
  if (machine.customer?.name) {
    subtitleParts.push(machine.customer.name);
  }
  if (machine.plant?.displayName) {
    subtitleParts.push(machine.plant.displayName);
  }
  const subtitle = subtitleParts.join(' · ') || undefined;

  return (
    <SidePanel
      open={open}
      onOpenChange={onOpenChange}
      title={machine.displayName || machine.machineId}
      subtitle={subtitle}
    >
      <SidePanelTabs
        defaultValue='event-log'
        className='flex h-full flex-col'
        enableSwipe
        tabOrder={machineTabOrder}
      >
        <SidePanelTabList>
          <SidePanelTab value='event-log'>Pulse alerts</SidePanelTab>
          <SidePanelTab value='details'>Details</SidePanelTab>
        </SidePanelTabList>

        <SidePanelTabContent value='details' className='flex-1 overflow-y-auto'>
          <MachineDetailsTab machine={machine} />
        </SidePanelTabContent>

        <SidePanelTabContent
          value='event-log'
          className='flex-1 overflow-y-auto'
        >
          <MachineEventLogTab machineId={machine.id} />
        </SidePanelTabContent>
      </SidePanelTabs>
    </SidePanel>
  );
}
